import { Button } from '@finpay-development/shared-components';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PriorCare, FacilityLevelOfCare, EstimateSummary } from '@finpay-estimation/estimation-types';
import { formatNumberToUSD } from '@finpay-development/shared-platform/lib/esm/shared/calculators';
import { TableColumn } from '../../../../shared/model/table-column';
import PriorCareSummaryTable from '../../prior-care-summary-table';

interface PriorCareSummaryModalProps {
    open: boolean;
    handleSummaryModalCancel: () => void;
    priorCare: PriorCare;
    facilityLevelOfCare: FacilityLevelOfCare[];
    priorCareSummary: EstimateSummary;
    summary: EstimateSummary;
}

const PriorCareSummaryModal = (props: PriorCareSummaryModalProps) => {
    const { open, handleSummaryModalCancel, priorCare, facilityLevelOfCare, priorCareSummary, summary } = props;

    const locTableHeaders: TableColumn[] = [
        { id: "levelOfCareCode", label: "Code", minWidth: 200 },
        { id: "levelOfCareName", label: "Name", minWidth: 200 },
        { id: "colLocRate", label: "Rate" },
        { id: "colLocDays", label: "Days/Sessions" },
    ];

    const newEstimatelocTableHeaders: TableColumn[] = [
        {id: 'colLocName', label: 'Levels of Care', align: 'left'},
        {
            id: 'colLocDays',
            label: 'Length of Stay (sessions/days)',
            align: 'left',
        },
        {id: 'colLocChgs', label: 'Adjusted Charges', align: 'right'},
    ];

    const estimatedAdjustedCharges = formatNumberToUSD(priorCareSummary.totalAdjustedCharges);

    const estimateTableRows = facilityLevelOfCare && Array.isArray(facilityLevelOfCare) && facilityLevelOfCare
        .filter((loc: any) => loc.locType !== 1)
        .map((row: any, index: number) => (
            <TableRow key={index} className="py-3">
                <TableCell>
                    {row.facilityLevelOfCareCode}-{row.facilityLevelOfCareName}
                </TableCell>
                <TableCell>{row.losDays}</TableCell>
                <TableCell align="right">
                    {formatNumberToUSD(row.totalCharge)}
                </TableCell>
            </TableRow>
        ));

    return (
        <Dialog
            className="modal"
            open={open}
            fullWidth={true}
            maxWidth="md"
            scroll="body"
        >
            <DialogTitle>
                Prior Care Summary
                <IconButton
                    aria-label="close"
                    onClick={handleSummaryModalCancel}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography variant="subtitle2">Prior Care Plan Details</Typography>
                <>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {locTableHeaders.map((column) => (
                                        <TableCell key={column.id}>{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {priorCare.loc && priorCare.loc
                                    .filter((loc: any) => loc.locType !== 1)
                                    .map((row: any, index: number) => (
                                        <TableRow key={`${row.facilityLevelOfCareCode}.${index}`}>
                                            <TableCell>{row.facilityLevelOfCareCode}</TableCell>
                                            <TableCell>{row.facilityLevelOfCareName}</TableCell>
                                            <TableCell>{row.pdrRate}</TableCell>
                                            <TableCell>{row.losDays}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <PriorCareSummaryTable priorCareSummary={priorCareSummary} />
                    </TableContainer>
                </>

                <Typography variant="subtitle2" style={{marginTop: "12px"}}>Care Plan Details</Typography>
                <TableContainer component={Paper}>
                    <Table size="small" className="alternating-row">
                        <TableHead>
                            <TableRow>
                                {newEstimatelocTableHeaders.map(column => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {estimateTableRows}
                            <TableRow className="py-3 light-green-row">
                                <TableCell colSpan={2}>
                                    Estimated Adjusted Charges
                                </TableCell>
                                <TableCell align="right">
                                    {estimatedAdjustedCharges}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3 light-green-row">
                                <TableCell colSpan={2}>
                                    Estimated Length of Stay
                                </TableCell>
                                <TableCell align="right">
                                    {priorCareSummary.lengthOfStay}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="subtitle2" className="mt-2">
                    Adjusted PFR Details
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small" className="alternating-row">
                        <TableBody>
                            <TableRow className="py-3">
                                <TableCell>Estimated Co-Pay</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        priorCareSummary.patientTotalCoPays
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell>Estimated Deductible</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        priorCareSummary.deductibleSpent
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell>Estimated Co-Insurance</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        priorCareSummary.patientTotalCoInsurance
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell></TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ borderTop: '2px solid #999' }}
                                >
                                    <strong>
                                        {formatNumberToUSD(
                                            priorCareSummary.coveredSvcsPfr
                                        )}
                                    </strong>
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell>
                                    Out of Pocket Max Remaining
                                </TableCell>
                                <TableCell align="right">
                                    {priorCareSummary.includeOOPmax ? (
                                        formatNumberToUSD(
                                            priorCareSummary.remainingOopMaxFromVob
                                        )
                                    ) : (
                                        <>N/A</>
                                    )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell>PFR for Covered Services</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(priorCareSummary.netPFR)}
                                </TableCell>
                            </TableRow>
                            <TableRow className="py-3">
                                <TableCell>Uncovered Services</TableCell>
                                <TableCell align="right">
                                    {formatNumberToUSD(
                                        priorCareSummary.unCoveredCharges
                                    )}
                                </TableCell>
                            </TableRow>
                            {priorCareSummary.totalSurcharges > 0 && (
                                <>
                                    <TableRow className="py-3">
                                        <TableCell>PFR Subtotal</TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                priorCareSummary.subtotalPFR
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="py-3">
                                        <TableCell>
                                            Admission Assessment
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatNumberToUSD(
                                                priorCareSummary.totalSurcharges
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </>
                            )}
                            <TableRow className="py-3">
                                <TableCell>
                                    <Typography variant="subtitle2">
                                        Estimated Patient Financial
                                        Responsibility
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="subtitle2">
                                        {formatNumberToUSD(
                                            priorCareSummary.totalPFR
                                        )}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions>
                <Button
                    type="secondary"
                    onClick={handleSummaryModalCancel}
                    test-id="prior-care-summary-modal-close-button"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PriorCareSummaryModal; 